// Client-side image compression run right before upload. Shrinks user-selected
// photos in the browser so uploads are fast on mobile and the server does less
// work. This is best-effort and must NEVER block an upload: if anything goes
// wrong (or the file can't be safely compressed) we return the ORIGINAL file.
//
// Note: the backend already resizes + WebPs every uploaded photo on save, so
// this is purely a transfer-size optimization. Identity/verification documents
// (passport, selfie) are intentionally NOT passed here by callers — moderation
// needs full resolution.

import imageCompression from 'browser-image-compression';
import {convertHeicIfNeeded} from './heicConvert';

// Sensible defaults for profile / request / feedback photos.
const DEFAULT_OPTIONS = {
    maxWidthOrHeight: 1600,
    maxSizeMB: 0.5,
    useWebWorker: true,
    initialQuality: 0.8,
};

// Identity / verification documents (passport, selfie, taxpayer) are compressed
// too, but gently — moderation must stay legible, so we keep a larger dimension
// and near-lossless quality. Still far smaller than a raw phone photo.
const DOCUMENT_OPTIONS = {
    maxWidthOrHeight: 2400,
    maxSizeMB: 2,
    useWebWorker: true,
    initialQuality: 0.92,
};

// HEIC/HEIF can't be decoded by the browser canvas, so compression would throw.
// The backend handles HEIC, so we leave those files untouched.
const isHeic = (file) => {
    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();

    return (
        type === 'image/heic' ||
        type === 'image/heif' ||
        name.endsWith('.heic') ||
        name.endsWith('.heif')
    );
};

// Only raster images that the browser can decode are worth compressing; PDFs
// and other non-images (and HEIC/HEIF) pass straight through.
const isCompressibleImage = (file) =>
    file instanceof File &&
    file.type.startsWith('image/') &&
    !isHeic(file);

// Compress a single file, falling back to the original on any failure or when
// the file isn't a compressible raster image. Resolves only — never rejects.
export const compressImage = async (file, options = {}) => {
    // iPhone HEIC can't be decoded by the canvas compressor (and the backend
    // rejects it) — convert to JPEG first so it's both compressible AND a valid
    // upload. Non-HEIC files pass straight through convertHeicIfNeeded.
    const input = await convertHeicIfNeeded(file);

    if (!isCompressibleImage(input)) {
        return input;
    }

    try {
        const compressed = await imageCompression(input, {
            ...DEFAULT_OPTIONS,
            ...options,
        });

        // browser-image-compression may resolve a Blob; normalize back to a
        // File so callers keep the original filename for the upload.
        if (compressed instanceof File) {
            return compressed;
        }

        return new File([compressed], input.name, {
            type: compressed.type || input.type,
            lastModified: Date.now(),
        });
    } catch {
        // Best-effort only: keep the (converted) file so the upload still succeeds.
        return input;
    }
};

// Compress a list of files, preserving order. Non-images / failures pass
// through unchanged.
export const compressImages = async (files) =>
    Promise.all(Array.from(files || []).map((file) => compressImage(file)));

// Compress an identity/verification document at near-lossless quality (keeps
// passports/selfies legible for moderation). PDFs and HEIC pass through.
export const compressDocument = (file) => compressImage(file, DOCUMENT_OPTIONS);

// Compress a list of documents, preserving order.
export const compressDocuments = async (files) =>
    Promise.all(Array.from(files || []).map((file) => compressDocument(file)));
